
import { Component, ElementRef, Input, OnInit, Output, ViewChild, EventEmitter, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { InventoryItemDetail, InventoryItem, SellerOfferAction, SellerOfferDto, multiLanguageAlphaNumericRegexHelper } from 'remarketing-angular-library';
import { takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import { UntypedFormGroup, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { OfferActionInterface } from '../../interfaces/offer-action.interface';
import { InventoryItemDataService } from './../../../sell-shared/services/inventory-item-data.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CurrencyFormatService } from 'smartauction-frontend-micro-library';
import {
  CharacterCountComponent,
  CharacterCountDirective,
  DatalistItemComponent
} from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'lib-decline',
    templateUrl: './decline.component.html',
    styleUrls: ['./decline.component.scss'],
    imports: [
        TranslocoDirective,
        DatalistItemComponent,
        CharacterCountComponent,
        ReactiveFormsModule,
        CommonModule,
        CharacterCountDirective
    ]
})
export class DeclineComponent implements OnInit, OnDestroy {
  private _isVisible = false;
  characterCountData: any;
  @Input() set isVisible(isVisible: boolean) {
    this._isVisible = isVisible;
    if (this._isVisible) {
      setTimeout(() => this.heading.nativeElement.focus());
    }
  }
  get isVisible() {
    return this._isVisible;
  }
  @ViewChild('heading') heading: ElementRef<HTMLHeadingElement>;
  @Input() inventoryItem: InventoryItem;
  @Input() offer: Partial<SellerOfferDto>;
  @Input() tenantName: string;
  @Output() cancel = new EventEmitter();
  @Output() action = new EventEmitter<OfferActionInterface>();

  commentForm: UntypedFormGroup;
  destroyed$ = new Subject();
  localErrorMessage: string;
  localErrorState$: Observable<{ message: string; offerUuid: string }> = this.inventoryItemDataService.localErrorState;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly inventoryItemDataService: InventoryItemDataService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.commentForm = this.formBuilder.group({
      offerComment: ['']
    });

    this.localErrorState$.pipe(takeUntil(this.destroyed$)).subscribe(localErrorState => {
      if (localErrorState.offerUuid) {
        localErrorState.offerUuid === this.offer.offerUuid
          ? (this.localErrorMessage = localErrorState.message)
          : (this.localErrorMessage = null);
      } else {
        this.localErrorMessage = null;
      }
    });
    this.translocoService.selectTranslateObject('UIComponents').subscribe(translatedObj => {
      this.characterCountData = translatedObj.characterCount;
    });
  }

  get title() {
    return `${this.detail('modelYear')} ${this.detail('make')} ${this.detail('model')} ${this.detail(
      'trim'
    )} VIN ${this.detail('vin')}`;
  }

  detail(key: keyof InventoryItemDetail): string {
    return get(this.itemDetails, key, '');
  }

  get itemDetails(): any | {} {
    return get(this.inventoryItem, 'itemDetail', {});
  }

  get(path: string, otherwise: any = 'N/A'): string {
    return get(this.offer, path, otherwise);
  }

  replaceInvalidCharacters(e) {
    e.target.value = multiLanguageAlphaNumericRegexHelper(e.target.value);
  }

  decline() {
    const payload = {
      offerUuid: this.offer.offerUuid,
      requestedAction: SellerOfferAction.DECLINE,
      comment: this.commentForm.get('offerComment').value,
      amount: this.offer.amount
    };
    const analyticsPayload = {
      formValues: {
        name: 'Decline Offer',
        status: null,
        errors: null,
        type: '',
        step: '1',
        stepname: '',
        items: [
          {
            allow: 'yes',
            error: null,
            name: 'Vehicle UUID',
            track: 'yes',
            value: this.inventoryItem ? this.inventoryItem.inventoryItemUuid : null
          },
          {
            allow: 'yes',
            error: null,
            name: 'Buyer Offer',
            track: 'yes',
            value: this.offer.amount
          }
        ]
      }
    };
    const data = {
      payload,
      analyticsPayload
    };
    this.action.emit(data);
  }

  formatCurrency(currency) {
    return this.currencyFormatService.formatCurrency(currency, false).replaceAll('-', '');
  }

  ngOnDestroy(): void {
    this.localErrorMessage = null;
    this.inventoryItemDataService._localErrorState.next({ message: null, offerUuid: null });

    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
